import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/lib/apiClient'

import type { PublicVoteCategory, VoteAccepted, VotingCategory } from './types'

function voterQuery(token: string, attendeeId: string): string {
  return `token=${encodeURIComponent(token)}&attendee_id=${encodeURIComponent(attendeeId)}`
}

/**
 * Ce que CE votant peut faire.
 *
 * `can_vote` et `reason` sont calculés côté serveur : le front s'en sert pour
 * griser un bouton et dire pourquoi, jamais pour s'autoriser lui-même. Chaque
 * vote est revérifié.
 */
export function useVotingCategories(token: string | null, attendeeId: string | null) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['voting-categories', token, attendeeId, i18n.language],
    queryFn: () =>
      apiClient.get<VotingCategory[]>(
        `/me/voting/categories?${voterQuery(token ?? '', attendeeId ?? '')}`,
        { locale: i18n.language },
      ),
    enabled: Boolean(token && attendeeId),
    // Un vote s'ouvre et se ferme au micro : l'écran doit suivre sans qu'on
    // demande à quelqu'un de rafraîchir sa page en pleine soirée.
    refetchInterval: 20_000,
  })
}

export function useCastVote(token: string | null, attendeeId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ categoryId, candidateId }: { categoryId: string; candidateId: string }) =>
      apiClient.post<VoteAccepted>(
        `/me/voting/${categoryId}/vote?${voterQuery(token ?? '', attendeeId ?? '')}`,
        { candidate_id: candidateId },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['voting-categories', token, attendeeId] })
    },
  })
}

export function useWithdrawVote(token: string | null, attendeeId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ categoryId, candidateId }: { categoryId: string; candidateId: string }) =>
      apiClient.delete<void>(
        `/me/voting/${categoryId}/vote/${candidateId}?${voterQuery(token ?? '', attendeeId ?? '')}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['voting-categories', token, attendeeId] })
    },
  })
}

/** Catégories et résultats **publiés** — lisibles sans billet. */
/**
 * `refetchInterval` est réglable : la page « Résultats » suit une annonce au
 * micro et doit rafraîchir plus vite que le reste du site, où une minute
 * suffit largement.
 */
export function usePublicVoteCategories(
  slug: string | undefined,
  options: { refetchInterval?: number } = {},
) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['public-vote-categories', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicVoteCategory[]>(`/public/events/${slug}/vote-categories`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
    refetchInterval: options.refetchInterval ?? 60_000,
  })
}
